import React, { useEffect, useMemo, useState } from "react";
import { api, formatErr, fmtDateTime, money, toDateTimeLocal } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, StatusPill, EmptyState } from "@/components/shared";
import ReservationsCalendar from "@/components/ReservationsCalendar";
import { Plus, Search, Edit3, Trash2, Send, MoveRight, List, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const RES_STATUSES = ["Reserved", "Picked Up", "Returned", "Overdue", "Cancelled"];

const empty = {
  customer_id: "",
  vehicle_id: "",
  pickup_datetime: "",
  return_datetime: "",
  rate_type: "daily",
  rate_amount: 0,
  deposit_paid: 0,
  status: "Reserved",
  notes: "",
};

export default function Reservations() {
  const { isAdmin } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");

  const load = async () => {
    const [r, c, v] = await Promise.all([api.get("/reservations"), api.get("/customers"), api.get("/vehicles")]);
    setRows(r.data);
    setCustomers(c.data);
    setVehicles(v.data);
  };
  useEffect(() => {
    load();
  }, []);

  const veh = (id) => vehicles.find((x) => x.id === id);

  // Auto-pick rate based on selected vehicle + rate type
  const pickRateFor = (vehicleId, rateType) => {
    const v = veh(vehicleId);
    if (!v) return 0;
    return rateType === "weekly" ? Number(v.weekly_rate || 0) : Number(v.daily_rate || 0);
  };

  const totalCost = useMemo(() => {
    if (!editing?.pickup_datetime || !editing?.return_datetime || !editing?.rate_amount) return 0;
    const p = new Date(editing.pickup_datetime);
    const r = new Date(editing.return_datetime);
    const ms = r - p;
    if (ms <= 0) return 0;
    const days = Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    const rate = Number(editing.rate_amount);
    if (editing.rate_type === "weekly") {
      const weeks = Math.max(1, Math.ceil(days / 7));
      return weeks * rate;
    }
    return days * rate;
  }, [editing]);

  const balanceDue = Math.max(0, totalCost - Number(editing?.deposit_paid || 0));

  const openNew = () => {
    setEditing({ ...empty });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing({
      ...empty,
      ...r,
      pickup_datetime: toDateTimeLocal(r.pickup_datetime),
      return_datetime: toDateTimeLocal(r.return_datetime),
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = { ...editing };
      if (!payload.customer_id || !payload.vehicle_id) return toast.error("Customer and vehicle are required");
      if (!payload.pickup_datetime) return toast.error("Pickup date required");
      payload.pickup_datetime = new Date(payload.pickup_datetime).toISOString();
      payload.return_datetime = payload.return_datetime ? new Date(payload.return_datetime).toISOString() : null;
      payload.rate_amount = Number(payload.rate_amount);
      payload.deposit_paid = Number(payload.deposit_paid);
      const id = payload.id;
      delete payload.id;
      delete payload.created_at;
      delete payload.customer_name;
      delete payload.customer_phone;
      delete payload.vehicle_label;
      delete payload.vehicle_plate;
      delete payload.total_cost;
      delete payload.rental_days;
      delete payload.amount_paid;
      delete payload.balance_due;
      delete payload.payment_status;
      delete payload.pickup_reminder_sent;
      delete payload.return_reminder_sent;
      if (id) {
        await api.put(`/reservations/${id}`, payload);
        toast.success("Reservation updated");
      } else {
        await api.post("/reservations", payload);
        toast.success("Reservation created · booking SMS queued");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.post(`/reservations/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this reservation?")) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success("Reservation deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter !== "All" && r.status !== statusFilter) return false;
    const text = `${r.customer_name} ${r.vehicle_label} ${r.vehicle_plate}`.toLowerCase();
    return text.includes(q.toLowerCase());
  });

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Reservations"
        subtitle="Bookings & rentals"
        testId="reservations-page-header"
        action={
          <Button data-testid="add-reservation-button" onClick={openNew} className="bg-black hover:bg-gray-800 text-white">
            <Plus size={16} className="mr-2" /> New Reservation
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by customer, vehicle…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="reservation-search-input" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["All", ...RES_STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`res-filter-${s.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                statusFilter === s ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="ml-auto inline-flex rounded-md border border-gray-200 overflow-hidden">
          <button
            onClick={() => setView("list")}
            data-testid="view-list"
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 ${view === "list" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            <List size={12} /> List
          </button>
          <button
            onClick={() => setView("calendar")}
            data-testid="view-calendar"
            className={`px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 border-l border-gray-200 ${view === "calendar" ? "bg-black text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            <CalendarDays size={12} /> Calendar
          </button>
        </div>
      </div>

      {view === "calendar" ? (
        <ReservationsCalendar reservations={filtered} />
      ) : filtered.length === 0 ? (
        <EmptyState title="No reservations" description="Create your first booking." testId="reservations-empty" action={<Button onClick={openNew}>Create reservation</Button>} />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="reservations-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Vehicle</th>
                  <th className="text-left px-4 py-3">Pickup</th>
                  <th className="text-left px-4 py-3">Return</th>
                  <th className="text-right px-4 py-3">Weekly Outstanding</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" data-testid={`reservation-row-${r.id}`} onClick={() => nav(`/admin/reservations/${r.id}`)}>
                    <td className="px-4 py-3 font-medium">{r.customer_name}</td>
                    <td className="px-4 py-3">
                      <div>{r.vehicle_label}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.vehicle_plate}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{fmtDateTime(r.pickup_datetime)}</td>
                    <td className="px-4 py-3 text-gray-700">{fmtDateTime(r.return_datetime)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(r.balance_due)}</td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                        <SelectTrigger className="h-8 w-[140px]" data-testid={`res-status-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RES_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Edit" data-testid={`edit-reservation-${r.id}`}>
                          <Edit3 size={13} />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => nav(`/admin/agreements/${r.id}`)} title="Agreement" data-testid={`agreement-reservation-${r.id}`}>
                          <MoveRight size={13} />
                        </Button>
                        {isAdmin && (
                          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => del(r.id)} data-testid={`delete-reservation-${r.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reservation-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing?.id ? "Edit Reservation" : "New Reservation"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Customer</Label>
                <Select value={editing.customer_id} onValueChange={(v) => setEditing({ ...editing, customer_id: v })}>
                  <SelectTrigger data-testid="res-customer-select"><SelectValue placeholder="Choose customer" /></SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}{c.phone ? ` · ${c.phone}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicle</Label>
                <Select value={editing.vehicle_id} onValueChange={(v) => {
                  const newRate = pickRateFor(v, editing.rate_type);
                  setEditing((s) => ({ ...s, vehicle_id: v, rate_amount: s.rate_amount && Number(s.rate_amount) > 0 ? s.rate_amount : newRate }));
                }}>
                  <SelectTrigger data-testid="res-vehicle-select"><SelectValue placeholder="Choose vehicle" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} {v.plate ? `· ${v.plate}` : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pickup date/time</Label>
                <Input type="datetime-local" data-testid="res-pickup" value={editing.pickup_datetime} onChange={(e) => setEditing({ ...editing, pickup_datetime: e.target.value })} />
              </div>
              <div>
                <Label>Return date/time (optional)</Label>
                <Input type="datetime-local" data-testid="res-return" value={editing.return_datetime} onChange={(e) => setEditing({ ...editing, return_datetime: e.target.value })} />
              </div>
              <div>
                <Label>Rate type</Label>
                <Select value={editing.rate_type} onValueChange={(v) => {
                  const newRate = pickRateFor(editing.vehicle_id, v);
                  setEditing((s) => ({ ...s, rate_type: v, rate_amount: s.rate_amount && Number(s.rate_amount) > 0 ? s.rate_amount : newRate }));
                }}>
                  <SelectTrigger data-testid="res-rate-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Rate amount ($)</Label>
                <Input type="number" step="0.01" data-testid="res-rate" value={editing.rate_amount} onChange={(e) => setEditing({ ...editing, rate_amount: e.target.value })} />
              </div>
              <div>
                <Label>Deposit paid ($)</Label>
                <Input type="number" step="0.01" data-testid="res-deposit" value={editing.deposit_paid} onChange={(e) => setEditing({ ...editing, deposit_paid: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="res-status-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RES_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 grid grid-cols-1 gap-3 bg-gray-50 border border-gray-200 rounded-md p-3">
                <div>
                  <div className="overline">Weekly outstanding</div>
                  <div className="font-heading text-xl font-semibold tabular-nums" data-testid="res-total-cost">{money(balanceDue)}</div>
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea rows={2} data-testid="res-notes" value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="res-cancel">Cancel</Button>
            <Button onClick={save} className="bg-black hover:bg-gray-800 text-white" data-testid="res-save">
              <Send size={14} className="mr-2" />
              {editing?.id ? "Save" : "Create & notify"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
